import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Download, Trash2, Search, Inbox } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { API } from "@/lib/site";

const STATUSES = [
  { value: "all", label: "All" },
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
];

const STATUS_LABEL = { new: "New", contacted: "Contacted", in_progress: "In Progress", completed: "Completed" };

const formatError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [enquiries, setEnquiries] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setUser(r.data))
      .catch(() => setUser(false));
  }, []);

  const load = async () => {
    const { data } = await axios.get(`${API}/enquiries`, { withCredentials: true });
    setEnquiries(data);
  };

  useEffect(() => {
    if (user) load().catch(() => toast.error("Could not load enquiries"));
  }, [user]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(false);
    setEnquiries([]);
  };

  const updateStatus = async (id, status) => {
    await axios.patch(`${API}/enquiries/${id}`, { status }, { withCredentials: true });
    setEnquiries((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));
    toast.success(`Marked as ${STATUS_LABEL[status]}`);
  };

  const remove = async (id) => {
    await axios.delete(`${API}/enquiries/${id}`, { withCredentials: true });
    setEnquiries((prev) => prev.filter((e) => e.id !== id));
    toast.success("Enquiry deleted");
  };

  const filtered = useMemo(
    () =>
      enquiries.filter((e) => {
        if (filter !== "all" && e.status !== filter) return false;
        if (!search) return true;
        const q = search.toLowerCase();
        return [e.name, e.email, e.service].some((v) => v?.toLowerCase().includes(q));
      }),
    [enquiries, filter, search]
  );

  const exportCsv = () => {
    const rows = [
      ["Name", "Email", "Phone", "Service", "Status", "Message", "Date"],
      ...filtered.map((e) => [e.name, e.email, e.phone, e.service, e.status, (e.message || "").replace(/\s+/g, " "), e.created_at]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "brickn-ridge-enquiries.csv";
    a.click();
  };

  if (user === null) {
    return (
      <div className="min-h-screen bg-[#0c0e12] flex items-center justify-center" data-testid="admin-loading">
        <Loader2 className="animate-spin text-orange-500" size={32} />
      </div>
    );
  }

  if (user === false) {
    return (
      <div className="min-h-screen bg-[#0c0e12] flex items-center justify-center px-6" data-testid="admin-login-page">
        <div className="grain-overlay" />
        <form onSubmit={login} className="w-full max-w-sm bg-[#14171f] border border-white/10 p-8" data-testid="admin-login-form">
          <div className="flex items-center gap-3 mb-8">
            <LogoMark size={36} />
            <div>
              <p className="font-display text-2xl text-white tracking-wider leading-none">BRICKN-RIDGE</p>
              <p className="font-mono text-[10px] tracking-[0.35em] text-slate-500 uppercase mt-1">Admin Access</p>
            </div>
          </div>
          <label htmlFor="admin-email" className="block font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-2">Email</label>
          <input
            id="admin-email"
            data-testid="admin-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-[#0c0e12] border border-white/15 focus:border-orange-500 focus:outline-none text-slate-100 text-sm px-4 py-3 mb-4"
          />
          <label htmlFor="admin-password" className="block font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-2">Password</label>
          <input
            id="admin-password"
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-[#0c0e12] border border-white/15 focus:border-orange-500 focus:outline-none text-slate-100 text-sm px-4 py-3"
          />
          {error && (
            <p className="mt-4 font-mono text-xs text-red-400 uppercase tracking-wider" data-testid="admin-login-error">{error}</p>
          )}
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={busy}
            className="mt-6 w-full inline-flex justify-center items-center gap-2 bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-[#0c0e12] font-mono text-sm font-bold uppercase tracking-[0.15em] px-6 py-3.5 transition-colors"
          >
            {busy && <Loader2 size={16} className="animate-spin" />}
            Sign In
          </button>
          <a href="/" data-testid="admin-back-home" className="mt-4 block text-center font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 hover:text-orange-500">
            Back to site
          </a>
        </form>
      </div>
    );
  }

  const stats = {
    total: enquiries.length,
    new: enquiries.filter((e) => e.status === "new").length,
    completed: enquiries.filter((e) => e.status === "completed").length,
  };

  return (
    <div className="min-h-screen bg-[#0c0e12] text-slate-100" data-testid="admin-dashboard">
      <header className="border-b border-white/10 px-6 lg:px-12 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <LogoMark size={32} />
          <div>
            <p className="font-display text-xl text-white tracking-wider leading-none">ENQUIRIES DASHBOARD</p>
            <p className="font-mono text-[10px] tracking-[0.3em] text-slate-500 uppercase mt-0.5">{user.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <a href="/" data-testid="admin-view-site" className="hidden sm:block font-mono text-xs uppercase tracking-[0.2em] text-slate-400 hover:text-orange-500">View site</a>
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            className="inline-flex items-center gap-2 border border-white/15 hover:border-orange-500 text-slate-300 hover:text-orange-500 font-mono text-xs uppercase tracking-[0.2em] px-4 py-2.5 transition-colors"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      <main className="px-6 lg:px-12 py-8 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-3 gap-3 mb-8">
          {[
            { label: "Total Enquiries", value: stats.total, testid: "admin-stats-total" },
            { label: "New Leads", value: stats.new, testid: "admin-stats-new" },
            { label: "Completed", value: stats.completed, testid: "admin-stats-completed" },
          ].map((s) => (
            <div key={s.label} className="bg-[#14171f] border border-white/10 p-4 sm:p-6" data-testid={s.testid}>
              <p className="font-display text-3xl sm:text-5xl text-orange-500">{s.value}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
          <div className="flex flex-wrap gap-2">
            {STATUSES.map((s) => (
              <button
                key={s.value}
                data-testid={`admin-filter-${s.value}`}
                onClick={() => setFilter(s.value)}
                className={`font-mono text-[11px] uppercase tracking-[0.15em] px-4 py-2 border transition-colors ${
                  filter === s.value
                    ? "bg-orange-600 border-orange-600 text-[#0c0e12] font-bold"
                    : "border-white/15 text-slate-400 hover:border-orange-500 hover:text-orange-500"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className="md:ml-auto flex items-center gap-3">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                data-testid="admin-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email, service"
                className="bg-[#14171f] border border-white/15 focus:border-orange-500 focus:outline-none text-sm text-slate-100 pl-9 pr-4 py-2.5 w-64 placeholder:text-slate-600"
              />
            </div>
            <button
              data-testid="admin-export-btn"
              onClick={exportCsv}
              className="inline-flex items-center gap-2 border border-white/15 hover:border-orange-500 text-slate-300 hover:text-orange-500 font-mono text-xs uppercase tracking-[0.15em] px-4 py-2.5 transition-colors"
            >
              <Download size={14} /> CSV
            </button>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="border border-white/10 bg-[#14171f] p-16 text-center" data-testid="admin-empty-state">
            <Inbox size={32} className="mx-auto text-slate-600 mb-4" />
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500">No enquiries match this view</p>
          </div>
        ) : (
          <div className="border border-white/10 overflow-x-auto">
            <table className="w-full text-left text-sm min-w-[760px]" data-testid="admin-enquiries-table">
              <thead>
                <tr className="bg-[#14171f] font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                  <th className="px-5 py-4">Client</th>
                  <th className="px-5 py-4">Contact</th>
                  <th className="px-5 py-4">Service</th>
                  <th className="px-5 py-4">Message</th>
                  <th className="px-5 py-4">Date</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => (
                  <tr key={e.id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`admin-row-${e.id}`}>
                    <td className="px-5 py-4 font-semibold text-white whitespace-nowrap">{e.name}</td>
                    <td className="px-5 py-4 text-slate-400">
                      <div className="text-xs">{e.email}</div>
                      <div className="text-xs text-slate-500">{e.phone}</div>
                    </td>
                    <td className="px-5 py-4 font-mono text-xs uppercase tracking-wider text-orange-500 whitespace-nowrap">{e.service}</td>
                    <td className="px-5 py-4 text-slate-400 text-xs max-w-[260px] truncate" title={e.message}>{e.message}</td>
                    <td className="px-5 py-4 font-mono text-xs text-slate-500 whitespace-nowrap">
                      {new Date(e.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-5 py-4">
                      <select
                        data-testid={`admin-status-select-${e.id}`}
                        value={e.status}
                        onChange={(ev) => updateStatus(e.id, ev.target.value)}
                        className="bg-[#0c0e12] border border-white/15 text-xs text-slate-200 px-2 py-1.5 focus:border-orange-500 focus:outline-none"
                      >
                        {STATUSES.slice(1).map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-5 py-4">
                      <button
                        data-testid={`admin-delete-${e.id}`}
                        onClick={() => remove(e.id)}
                        className="text-slate-500 hover:text-red-400 transition-colors"
                        aria-label="Delete enquiry"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
